import { Link } from '../Link';
import {
  InteractiveTagContext,
  InteractiveTagProvider,
} from './InteractiveTagContext';
import { TooltipWrapper } from '../../display/TooltipWrapper';

import styles from './InteractiveTagWrapper.module.scss';

export type InteractiveTagWrapperProps = {
  /**
   * The content to render within `InteractiveTagWrapper`.
   */
  children: React.ReactNode;
  /**
   * The color of `InteractiveTag`.
   */
  color: 'primary' | 'secondary';
  /**
   * Optional `href` attribute to apply to `InteractiveTagWrapper`
   */
  href?: string;
  /**
   * Optional max-width of the `InteractiveTagWrapper`.
   * Must be set to a concrete value (i.e. not 100%) to support truncating long content.
   */
  maxWidth?: string;
  /**
   * Optional function to call when `InteractiveTag` is clicked.
   */
  onClick?: React.MouseEventHandler;
};

/**
 * Component used to wrap around `InteractiveTag.Content` and `InteractiveTag.Icon`.
 */
export const InteractiveTagWrapper = ({
  children,
  color,
  href,
  maxWidth = 'none',
  onClick,
}: InteractiveTagWrapperProps) => {
  const sharedProps = {
    className: styles.root,
    'data-color': color,
    onClick,
    style: {
      '--_zds-interactive-tag-wrapper-max-width': maxWidth,
    },
  };

  const WrappedChildren = () => {
    if (href) {
      return (
        <Link {...sharedProps} color={null} href={href} weight={null}>
          {children}
        </Link>
      );
    }

    return (
      <button {...sharedProps} type="button" data-zds>
        {children}
      </button>
    );
  };

  return (
    <InteractiveTagProvider>
      <InteractiveTagContext.Consumer>
        {(context) => {
          return context?.isTruncated ? (
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={
                context.body
                  ? `${context.title}: ${context.body}`
                  : `${context.title}`
              }
              shouldCorrectPosition={true}
            >
              {() => <WrappedChildren />}
            </TooltipWrapper>
          ) : (
            <WrappedChildren />
          );
        }}
      </InteractiveTagContext.Consumer>
    </InteractiveTagProvider>
  );
};
